/** @file Mesh topology (createMesh, commands-mesh): the image quad by default, else checked uvs/triangles. */
import { meshTopologyProblem } from '@pose2d/format/doc/meshTopologyProblem';
import { throwError } from '@pose2d/format/throwError';

/** A rigid mesh's topology: uvs (0..1, y down, per vertex), index triples, outline count, bone-space vertices. */
export type MeshTopology = { uvs: number[]; triangles: number[]; hull: number; vertices: number[] };
/** What createMesh accepts; uvs, triangles and hull come together or not at all. */
export type MeshTopologyInput = { uvs?: number[]; triangles?: number[]; hull?: number; vertices?: number[]; edges?: number[] };
type Size = { w: number; h: number };

/** Bone-space vertices for normalized y-down uvs: the image centred on the bone origin, y up. */
const fromUvs = (uvs: readonly number[], size: Size): number[] => uvs.map((v, i) => (i % 2 === 0 ? (v - 0.5) * size.w : (0.5 - v) * size.h));

/** The image quad (TL, BL, BR, TR); VALIDATION at the first missing field when only part of a topology was given. */
function defaultQuad(input: MeshTopologyInput, size: Size): MeshTopology {
  const partial = (['uvs', 'triangles', 'hull', 'vertices', 'edges'] as const).some((f) => input[f] !== undefined);
  if (partial) {
    const missing = (['uvs', 'triangles', 'hull'] as const).find((f) => input[f] === undefined) ?? 'uvs';
    throwError('VALIDATION', undefined, { path: missing, hint: 'give uvs, triangles and hull together, or none of them for the image quad' });
  }
  const uvs = [0, 0, 0, 1, 1, 1, 1, 0];
  return { uvs, triangles: [0, 1, 2, 2, 3, 0], hull: 4, vertices: fromUvs(uvs, size) };
}

/** VALIDATION at the first field that breaks the format's one topology rule (validateDoc applies the same rule). */
function checkTopology(t: MeshTopology, edges: readonly number[] | undefined): void {
  const problem = meshTopologyProblem({ ...t, edges });
  if (problem) throwError('VALIDATION', undefined, { path: problem[0], hint: problem[1] });
}

/** The image quad when uvs/triangles/hull are all omitted; otherwise the given topology, checked, with vertices derived from uvs when absent. */
export function meshTopology(input: MeshTopologyInput, size: Size): MeshTopology {
  const { uvs, triangles, hull } = input;
  if (uvs === undefined || triangles === undefined || hull === undefined) return defaultQuad(input, size);
  const topology = { uvs, triangles, hull, vertices: input.vertices ?? fromUvs(uvs, size) };
  checkTopology(topology, input.edges);
  return topology;
}
